import React from 'react';
import { makeStyles } from 'design-system/styles';
import { Button, Checkbox, CircularProgress, H1, H2 } from 'design-system';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { InputText } from 'design-system/Form';
import { parseObjectIds } from '../lib/parseInputText';
import HideableSection from '../components/UI/HideableSection';
import DatePicker from 'design-system/DatePicker';
import moment from 'moment';
import SelectProject from '../components/UI/SelectProject';

const TAXONOMY_PROJECT_ID = '604a72cbdf4670001e304ced';
const BRAND_PROJECT_ID = '607f8fd2430a900033afee82';

const ImbeUtilsPage: React.FC = () => {
  const styles = useStyles();
  return (
    <div className={styles.container}>
      <H1>Imbe Taxonomy Utils</H1>

      <HideableSection name="Long Hints and Definitions" forceHidden={true}>
        <UpdateLongHintsTool />
        <UpdateNodeDefinitionsTool />
      </HideableSection>

      <HideableSection name="Taxonomy Update" forceHidden={true}>
        <TaxonomyDiffTool />
        <TaxonomySaveTool />
        <TaxonomyUpdateTool />
        <TaxonomyBMUpdateTool />
      </HideableSection>

      <HideableSection name="Model Utilities" forceHidden={true}>
        <UploadMXQACorrectionsTool />
        <BackfillTaxonomyHypothesisTool />
        <BackfillTaxonomyHypothesisTestTool />
        <BackfillModelOntoCompletedTasks />
        <GenerateModelDataTool />
      </HideableSection>

      <HideableSection name="Project Utilities" forceHidden={true}>
        <MoveTasksToProjectTool />
      </HideableSection>

      <HideableSection name="Task Return" forceHidden={true}>
        <BulkCompletionTool />
        <BulkMoveReviewLevelTool />
        <SetTasksAsReturnedTool />
        <SetCorrectionsAsSentTool />
      </HideableSection>
    </div>
  );
};

const UpdateLongHintsTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/update-long-hints`, {
      method: 'POST',
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Taxonomy Long Hints</H2>
      <p>
        Update the long hints in production. Tool will read from the Google Sheet linked below,
        convert to a JSON, and upload to S3. Product tasks will read from the S3 link. May take up
        to an hour to deploy due to S3 overwriting.
      </p>
      <a href="https://docs.google.com/spreadsheets/d/1eO9BCRbs4t8LKYD7EtyJckLsQbNQ6GSznNfIYy48bPs/edit#gid=888631407">
        View SSOT
      </a>
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      {!isLoading && <Button onClick={execute}>{'Update'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const UpdateNodeDefinitionsTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const [backfillPendingTasks, setBackfillPendingTasks] = React.useState<boolean>(false);

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/update-node-definitions`, {
      method: 'POST',
      body: {
        backfillPendingTasks,
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Taxonomy Node Definitions</H2>
      <p>
        Update the node definitions (little i next to a node) for taxonomy tasks. If Backfill
        Pending, all currently pending subtasks will be updated. Otherwise, only newly ingested
        tasks will be updated.
      </p>
      <a href="https://docs.google.com/spreadsheets/d/1eO9BCRbs4t8LKYD7EtyJckLsQbNQ6GSznNfIYy48bPs/edit#gid=888631407">
        View SSOT (nodedefinitions sheet)
      </a>
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}

      <Checkbox
        edge="start"
        label={'Backfill all pending tasks?'}
        checked={backfillPendingTasks}
        onChange={e => setBackfillPendingTasks(e.currentTarget.checked)}
      />
      {!isLoading && <Button onClick={execute}>{'Update'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  toolContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: 800,
    marginTop: 16,
    '& > *': {
      marginTop: 8,
    },
  },
  spinner: {
    width: 800,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
});

const TaxonomyDiffTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/diff-taxonomies`, {
      method: 'POST',
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Diff Prod and Instacart Taxonomies</H2>
      <p>
        <b>Taxonomy Update Step 1a</b> <br />
        For current full update process, please see{' '}
        <a>
          https://docs.google.com/document/d/1rhOIhGGNHstQxaKDBk1bImfI7mskYH8-AHWGkFi4AnM/edit#bookmark=id.mrd49tfsv1t1
        </a>
        <br /> <br />
        Diff the current production taxonomy and the latest Instacart one (fetched via API). The
        diff and taxonomies will be produced in a Google Sheet.
      </p>
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      {!isLoading && <Button onClick={execute}>{'Create Diff'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const TaxonomySaveTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/save-latest-taxonomy`, {
      method: 'POST',
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Save Latest Taxonomy to Project Params</H2>
      <p>
        <b>Taxonomy Update Step 1b</b> <br />
        Now you're satisfied with the latest taxonomy, but need to make preparations{' '}
        <b>before using the taxonomy in Prod.</b> <br />
        This tool saves the latest taxonomy (fetched via API) into our{' '}
        <code>project.paramHistory</code> so it could be retrieved later. <br />
      </p>
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      {!isLoading && <Button onClick={execute}>{'Save Latest Taxonomy to Project Params'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const TaxonomyUpdateTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');
  const [dryRun, setDryRun] = React.useState<boolean>(true);
  const [PPV, setPPV] = React.useState<string>('');

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/update-taxonomies`, {
      method: 'POST',
      body: {
        dryRun,
        PPV,
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Update Prod Taxonomy + Backfill Prod Taxonomy</H2>
      <p>
        Make latest saved taxonomy the default for all newly ingested tasks, by setting{' '}
        <code>project.metadata.primaryParamVersion</code> to the provided PPV. <br />
        <br />
        Please use the appropriate PPV from <code>project.paramHistory</code>, making sure its{' '}
        <code>createdAt</code>, <code>taskParams</code>, and <code>taxonomyDiffSheetsLink</code> are
        what you expect. <br /> <br />
        Critically, runs the entire taxonomy update -- backfilling BMs, pending tasks, and task
        attempts!
      </p>
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      <Checkbox
        edge="start"
        label={'Dry Run?'}
        checked={dryRun}
        onChange={e => setDryRun(e.currentTarget.checked)}
      />
      PPV (desired param version from project.paramHistory)
      <InputText type="text" defaultValue={PPV} onChange={e => setPPV(e.target.value)} />
      {!isLoading && <Button onClick={execute}>{'Update'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const TaxonomyBMUpdateTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');
  const [dryRun, setDryRun] = React.useState<boolean>(true);
  const [bmIds, setBmIds] = React.useState<string>('');

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/update-taxonomy-bms`, {
      method: 'POST',
      body: {
        dryRun,
        bmIds: parseObjectIds(bmIds),
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Validate and Update BMs to Prod Taxonomy </H2>
      <p>
        Update BMs to use the prod taxonomy! <br />
        <b>
          Please use this immediately after creating new Instacart BMs. Newly created BMs will have
          the attachments and fields of their original subtasks, so they must be updated before use!{' '}
        </b>
        <br />
        This tool will attempt to update BMs and disable BMs that cannot be updated. If no BM ids
        are specified, the tool will attempt to update all BMs. <br />
        Use Dry Run to see how many BMs will possibly be updated! <br />
        Reasons BMs may be unupdatable include: a) we can't mock the attachment responses, because
        its original subtask's task does not have model predictions, b) task is not unjudgeable yet
        is missing taxonomy field, c) expected response cannot be translated to new taxonomy. If
        your BM gets disabled, please consider creating a different BM from a subtask that satisfies
        the conditions. <br />
      </p>
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      <Checkbox
        edge="start"
        label={'Dry Run?'}
        checked={dryRun}
        onChange={e => setDryRun(e.currentTarget.checked)}
      />
      Benchmarks To Update (ids - delimit any way you want)
      <InputText type="text" defaultValue={bmIds} onChange={e => setBmIds(e.target.value)} />
      {!isLoading && <Button onClick={execute}>{'Update'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const UploadMXQACorrectionsTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const [dryRun, setDryRun] = React.useState<boolean>(false);
  const [gsheetLink, setGsheetLink] = React.useState<string>('');
  const [gsheetName, setGsheetName] = React.useState<string>('');
  const [project, setProject] = React.useState<{ label: string; value: string }>({
    label: TAXONOMY_PROJECT_ID,
    value: TAXONOMY_PROJECT_ID,
  });

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/upload-taxonomy-corrections`, {
      method: 'POST',
      body: {
        dryRun,
        gsheetLink,
        gsheetName,
        projectId: project.value,
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Upload MXQA Corrections Tool</H2>
      <p>
        This tool uploads MXQA corrections from a Google Sheet. <br />
        To avoid timeouts, please limit the number of subtask rows to 10k.
      </p>
      <Checkbox
        edge="start"
        label={'Dry Run?'}
        checked={dryRun}
        onChange={e => setDryRun(e.currentTarget.checked)}
      />
      Project (default: '{TAXONOMY_PROJECT_ID}')
      <SelectProject placeholder="Select a project" onChange={setProject} value={project} />
      Google Sheets Link
      <InputText
        type="text"
        defaultValue={gsheetLink}
        onChange={e => setGsheetLink(e.target.value)}
      />
      Sheet Name
      <InputText
        type="text"
        defaultValue={gsheetName}
        onChange={e => setGsheetName(e.target.value)}
      />
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      {!isLoading && <Button onClick={execute}>{'Apply Corrections'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const BackfillTaxonomyHypothesisTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const [modelName, setModelName] = React.useState<string>('');
  const [overwriteHypothesis, setOverwriteHypothesis] = React.useState<boolean>(false);
  const [project, setProject] = React.useState<{ label: string; value: string }>({
    label: TAXONOMY_PROJECT_ID,
    value: TAXONOMY_PROJECT_ID,
  });

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/start-hypothesis-backfill`, {
      method: 'POST',
      body: {
        modelName,
        overwriteHypothesis,
        projectId: project.value,
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Start Hypothesis Backfill Tool</H2>
      <p>
        This tool starts hypothesis backfills for roberta, fasttext, and unjudgeable models on all
        pending tasks.
      </p>
      Project (default: '{TAXONOMY_PROJECT_ID}')
      <SelectProject placeholder="Select a project" onChange={setProject} value={project} />
      Roberta Model Name (please use latest)
      <InputText
        type="text"
        defaultValue={modelName}
        onChange={e => setModelName(e.target.value)}
      />
      <Checkbox
        edge="start"
        label={'Overwrite Hypothesis?'}
        checked={overwriteHypothesis}
        onChange={e => setOverwriteHypothesis(e.currentTarget.checked)}
      />
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      {!isLoading && <Button onClick={execute}>{'Backfill'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const BackfillTaxonomyHypothesisTestTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const [modelName, setModelName] = React.useState<string>('');
  const [numTasksToTest, setNumTasksToTest] = React.useState<string>('');

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/test-hypothesis-backfill`, {
      method: 'POST',
      body: {
        modelName,
        numTasksToTest,
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Test Hypothesis Backfill Tool</H2>
      <p>
        This tool tests hypothesis backfills for roberta, fasttext, and unjudgeable models on a
        specified number of tasks.
      </p>
      Roberta Model Name (please use latest)
      <InputText
        type="text"
        defaultValue={modelName}
        onChange={e => setModelName(e.target.value)}
      />
      Number of tasks
      <InputText
        type="text"
        defaultValue={numTasksToTest}
        onChange={e => setNumTasksToTest(e.target.value)}
      />
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      {!isLoading && <Button onClick={execute}>{'Backfill'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const BackfillModelOntoCompletedTasks = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const [modelName, setModelName] = React.useState<string>('');
  const [completedAtAfterDate, setCompletedAtAfterDate] = React.useState<moment.Moment>(moment());
  const [completedAtBeforeDate, setCompletedAtBeforeDate] = React.useState<moment.Moment>(moment());
  const [useTrainingEndpoint, setUseTrainingEndpoint] = React.useState<boolean>(false);

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/backfill-model-onto-completed-tasks`, {
      method: 'POST',
      body: {
        modelName,
        completedAtAfterDate: completedAtAfterDate.toISOString(),
        completedAtBeforeDate: completedAtBeforeDate.toISOString(),
        useTrainingEndpoint,
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Backfill Model Onto Completed Tasks</H2>
      <p>
        This tool backfills the latest Roberta ML model data onto tasks completed after some date.{' '}
      </p>
      Roberta Model Name (please use latest)
      <InputText
        type="text"
        defaultValue={modelName}
        onChange={e => setModelName(e.target.value)}
      />
      Task Completed After Date
      <DatePicker
        selected={completedAtAfterDate}
        onChange={date => setCompletedAtAfterDate(date || moment())}
        placeholderText={moment(completedAtAfterDate).format('MM/DD/YYYY')}
      />
      Task Completed Before Date
      <DatePicker
        selected={completedAtBeforeDate}
        onChange={date => setCompletedAtBeforeDate(date || moment())}
        placeholderText={moment(completedAtBeforeDate).format('MM/DD/YYYY')}
      />
      <Checkbox
        edge="start"
        label={'Use ML training endpoint?'}
        checked={useTrainingEndpoint}
        onChange={e => setUseTrainingEndpoint(e.currentTarget.checked)}
      />
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      {!isLoading && <Button onClick={execute}>{'Backfill'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const GenerateModelDataTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const TRANSLATIONS_SSOT =
    'https://docs.google.com/spreadsheets/d/1Xm5GQgB1LZ5b4MNKPzFFTDPromCp78JndVZl-0CzuhA/edit#gid=0';
  const [gsheetLink, setGsheetLink] = React.useState<string>(TRANSLATIONS_SSOT);
  const [gsheetName, setGsheetName] = React.useState<string>('translationsWithDeletions');
  const [completedAtAfterDate, setCompletedAtAfterDate] = React.useState<moment.Moment>(
    moment().subtract(10, 'days'),
  );

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/generate-model-data`, {
      method: 'POST',
      body: {
        gsheetLink,
        gsheetName,
        completedAtAfterDate: completedAtAfterDate.toISOString(),
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Generate Model Data Tool</H2>
      <p>This tool generates instacart model data and uploads it to s3</p>
      <p>
        Translations SSOT: <br />
        {TRANSLATIONS_SSOT}
      </p>
      Translations Google Sheets Link
      <InputText
        type="text"
        defaultValue={gsheetLink}
        onChange={e => setGsheetLink(e.target.value)}
      />
      Translations Sheet Name
      <InputText
        type="text"
        defaultValue={gsheetName}
        onChange={e => setGsheetName(e.target.value)}
      />
      Task Completed After Date
      <DatePicker
        selected={completedAtAfterDate}
        onChange={date => setCompletedAtAfterDate(date || moment())}
        placeholderText={moment(completedAtAfterDate).format('MM/DD/YYYY')}
      />
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      {!isLoading && <Button onClick={execute}>{'Generate'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const MoveTasksToProjectTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const [taskIds, setTaskIds] = React.useState<string>('');
  const [project, setProject] = React.useState<{ label: string; value: string }>({
    label: TAXONOMY_PROJECT_ID,
    value: TAXONOMY_PROJECT_ID,
  });

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/move-tasks-to-project`, {
      method: 'POST',
      body: {
        taskIds,
        projectId: project.value,
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Move Tasks Between Projects</H2>
      <p>
        Moves tasks from one project to another. <br />
        For example, from a main project to a cloned project, or vice versa.
      </p>
      <p></p>
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      Task Ids
      <InputText type="text" defaultValue={taskIds} onChange={e => setTaskIds(e.target.value)} />
      Project to move tasks to
      <SelectProject placeholder="Select a project" onChange={setProject} value={project} />
      {!isLoading && <Button onClick={execute}>{'Move tasks'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const BulkCompletionTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const [gsheetLink, setGsheetLink] = React.useState<string>('');
  const [gsheetName, setGsheetName] = React.useState<string>('');

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/bulk-send-to-customer`, {
      method: 'POST',
      body: {
        gsheetLink,
        gsheetName,
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Bulk Set Tasks Completed</H2>
      <p>
        This tool sets all tasks from the google sheet to completed, so it could be sent to
        customer. Batches of 30k
      </p>
      <p>
        Sample sheet:
        https://docs.google.com/spreadsheets/d/1G4UK5TFUtRc02ekFyWwN0EmSnT0uqrwyNr9Z39QqPfc/edit#gid=614909546
      </p>
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      Task Ids Google Sheets Link
      <InputText
        type="text"
        defaultValue={gsheetLink}
        onChange={e => setGsheetLink(e.target.value)}
      />
      Task Ids Sheet Name
      <InputText
        type="text"
        defaultValue={gsheetName}
        onChange={e => setGsheetName(e.target.value)}
      />
      {!isLoading && <Button onClick={execute}>{'Update'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const BulkMoveReviewLevelTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const [gsheetLink, setGsheetLink] = React.useState<string>('');
  const [gsheetName, setGsheetName] = React.useState<string>('');
  const [destinationReviewLevel, setDestinationReviewLevel] = React.useState<string>('');

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/bulk-move-review-level`, {
      method: 'POST',
      body: {
        gsheetLink,
        gsheetName,
        destinationReviewLevel,
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Bulk Move Review Level</H2>
      <p>This bulk moves subtasks between review levels. Batches of 30k</p>
      <p>
        Sample sheet (must be public to the web):
        https://docs.google.com/spreadsheets/d/1ANqrCCLWKolqIyJhCYRQyxIXM6vaXm173oSDfQurYrs/edit#gid=0
      </p>
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      Subtask Ids Google Sheets Link
      <InputText
        type="text"
        defaultValue={gsheetLink}
        onChange={e => setGsheetLink(e.target.value)}
      />
      Subtask Ids Sheet Name
      <InputText
        type="text"
        defaultValue={gsheetName}
        onChange={e => setGsheetName(e.target.value)}
      />
      Destination Review Level
      <InputText
        type="text"
        defaultValue={destinationReviewLevel}
        onChange={e => setDestinationReviewLevel(e.target.value)}
      />
      {!isLoading && <Button onClick={execute}>{'Move'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const SetTasksAsReturnedTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const [project, setProject] = React.useState<{ label: string; value: string }>({
    label: BRAND_PROJECT_ID,
    value: BRAND_PROJECT_ID,
  });
  const [taskIds, setTaskIds] = React.useState<string>('');
  const [dryRun, setDryRun] = React.useState<boolean>(true);

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/set-tasks-as-returned-tool`, {
      method: 'POST',
      body: {
        projectId: project.value,
        taskIds,
        dryRun,
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Set Tasks As Returned Tool</H2>
      <p>
        We sometimes return <strong>tasks</strong> to Instacart manually and want to mark them as
        such. This tool sets <code>task.metadata.instacartReturnedAt</code> on tasks
      </p>
      Project (default: '{BRAND_PROJECT_ID}')
      <SelectProject placeholder="Select a project" onChange={setProject} value={project} />
      Task Ids
      <InputText type="text" defaultValue={taskIds} onChange={e => setTaskIds(e.target.value)} />
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      <Checkbox
        edge="start"
        label={'Dry Run?'}
        checked={dryRun}
        onChange={e => setDryRun(e.currentTarget.checked)}
      />
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      {!isLoading && <Button onClick={execute}>{'Set tasks as returned'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

const SetCorrectionsAsSentTool = () => {
  const styles = useStyles();

  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>('');

  const [project, setProject] = React.useState<{ label: string; value: string }>({
    label: TAXONOMY_PROJECT_ID,
    value: TAXONOMY_PROJECT_ID,
  });
  const [taskIds, setTaskIds] = React.useState<string>('');
  const [dryRun, setDryRun] = React.useState<boolean>(true);

  const execute = () => {
    setLoading(true);
    apiFetch(null, `corp-api/imbe-utils/set-corrections-as-sent-tool`, {
      method: 'POST',
      body: {
        projectId: project.value,
        taskIds,
        dryRun,
      },
    })
      .then(result => {
        setResult(result.message);
        setLoading(false);
        return;
      })
      .catch(defaultApiFetchErrorHandler);
  };

  return (
    <div className={styles.toolContainer}>
      <H2>Set Corrections As Sent Tool</H2>
      <p>
        We sometimes send <strong>task corrections</strong> to Instacart manually and want to mark
        them as such. This tool sets <code>task.metadata.correctionSentAt</code> on tasks
      </p>
      Project (default: '{TAXONOMY_PROJECT_ID}')
      <SelectProject placeholder="Select a project" onChange={setProject} value={project} />
      Task Ids
      <InputText type="text" defaultValue={taskIds} onChange={e => setTaskIds(e.target.value)} />
      <Checkbox
        edge="start"
        label={'Dry Run?'}
        checked={dryRun}
        onChange={e => setDryRun(e.currentTarget.checked)}
      />
      {isLoading && (
        <div className={styles.spinner}>
          <CircularProgress />
        </div>
      )}
      {!isLoading && <Button onClick={execute}>{'Set task corrections as sent'}</Button>}
      {result && <p>{result}</p>}
    </div>
  );
};

export default ImbeUtilsPage;
